/**
 * AI PR Review Hooks
 *
 * React Query hooks backing the PR review list/detail look-back UI. Reviews
 * are read-only from the app's perspective — they're created by triggers
 * (publish / `@bot review` mention), not by any UI action here.
 */

import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type { PrReviewDetailResponse, PrReviewListResponse } from './types';

/** Paginated, skinny list of PR reviews for the viewer's org, newest first. */
export function usePrReviews(options: { cursor?: string; limit?: number }) {
  return useQuery({
    queryKey: ['pr-reviews', options.cursor, options.limit],
    queryFn: async (): Promise<PrReviewListResponse> => {
      const params = new URLSearchParams();
      if (options.cursor) params.set('cursor', options.cursor);
      if (options.limit) params.set('limit', String(options.limit));

      const response = await fetch(`${API_URL}/pr-reviews?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch PR reviews');
      }
      return response.json();
    },
    placeholderData: (previous) => previous,
  });
}

/** Full detail for a single PR review, including `bodyMarkdown` and `reportJson`. */
export function usePrReview(prReviewId: string | undefined) {
  return useQuery({
    queryKey: ['pr-review', prReviewId],
    queryFn: async (): Promise<PrReviewDetailResponse> => {
      if (!prReviewId) throw new Error('Missing PR review ID');

      const response = await fetch(`${API_URL}/pr-reviews/${prReviewId}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch PR review');
      }
      return response.json();
    },
    enabled: !!prReviewId,
  });
}
